import { useState, useEffect, useMemo } from 'react';
import api from '@/api';
import {
  Calendar, Shield, ChevronDown, ChevronRight, Filter,
  AlertTriangle, CheckCircle2, Clock, X, Download
} from 'lucide-react';

const STATUS_COLORS = {
  active: '#10b981',
  expiring_soon: '#f59e0b',
  expired: '#ef4444',
  pending: '#64748b',
};

function WarrantyBar({ w, timeRange, onClick, selected }) {
  const { minDate, maxDate, totalDays } = timeRange;
  if (!w.warranty_start || !w.warranty_end || totalDays <= 0) return null;

  const start = new Date(w.warranty_start);
  const end = new Date(w.warranty_end);
  const startOffset = Math.max(0, (start - minDate) / (1000 * 60 * 60 * 24));
  const duration = Math.max(7, (end - start) / (1000 * 60 * 60 * 24));

  const left = (startOffset / totalDays) * 100;
  const width = Math.min((duration / totalDays) * 100, 100 - left);
  const color = STATUS_COLORS[w.status] || STATUS_COLORS.pending;

  return (
    <div className="flex items-center gap-3 h-8 group" data-testid={`calendar-bar-${w.id}`}>
      <div className="w-48 shrink-0 truncate text-xs text-muted-foreground group-hover:text-foreground transition-colors cursor-pointer"
        onClick={onClick}>
        {w.equipment_name}
      </div>
      <div className="flex-1 relative h-5">
        <div
          className={`absolute h-full rounded-sm cursor-pointer transition-all ${selected ? 'ring-2 ring-orange-500' : 'hover:brightness-125'}`}
          style={{ left: `${left}%`, width: `${Math.max(width, 0.5)}%`, backgroundColor: color, opacity: 0.8 }}
          onClick={onClick}
          title={`${w.equipment_name}\n${w.warranty_start} → ${w.warranty_end}\n${w.days_remaining != null ? `${w.days_remaining} days remaining` : ''}`}
        />
      </div>
    </div>
  );
}

export default function WarrantyCalendar({ projectId, substantialCompletion }) {
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/warranties`)
      .then(r => setWarranties(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const filtered = statusFilter === 'all'
    ? warranties.filter(w => w.warranty_start && w.warranty_end)
    : warranties.filter(w => w.warranty_start && w.warranty_end && w.status === statusFilter);

  const timeRange = useMemo(() => {
    if (filtered.length === 0) return { minDate: new Date(), maxDate: new Date(), totalDays: 365 };
    const starts = filtered.map(w => new Date(w.warranty_start));
    const ends = filtered.map(w => new Date(w.warranty_end));
    if (substantialCompletion) starts.push(new Date(substantialCompletion));

    const minDate = new Date(Math.min(...starts));
    const maxDate = new Date(Math.max(...ends));
    // Add some padding
    minDate.setMonth(minDate.getMonth() - 1);
    maxDate.setMonth(maxDate.getMonth() + 2);
    const totalDays = (maxDate - minDate) / (1000 * 60 * 60 * 24);
    return { minDate, maxDate, totalDays };
  }, [filtered, substantialCompletion]);

  // Generate year markers
  const yearMarkers = useMemo(() => {
    const markers = [];
    const startYear = timeRange.minDate.getFullYear();
    const endYear = timeRange.maxDate.getFullYear();
    for (let y = startYear; y <= endYear; y++) {
      const d = new Date(y, 0, 1);
      const offset = (d - timeRange.minDate) / (1000 * 60 * 60 * 24);
      const pct = (offset / timeRange.totalDays) * 100;
      if (pct >= 0 && pct <= 100) {
        markers.push({ year: y, pct });
      }
    }
    return markers;
  }, [timeRange]);

  // Milestone markers
  const milestones = useMemo(() => {
    const ms = [];
    const now = new Date();
    const nowOffset = (now - timeRange.minDate) / (1000 * 60 * 60 * 24);
    ms.push({ label: 'Today', pct: (nowOffset / timeRange.totalDays) * 100, color: '#f97316' });

    if (substantialCompletion) {
      const sc = new Date(substantialCompletion);
      const scOffset = (sc - timeRange.minDate) / (1000 * 60 * 60 * 24);
      ms.push({ label: 'SC', pct: (scOffset / timeRange.totalDays) * 100, color: '#3b82f6' });

      // Month-11 walk
      const m11 = new Date(sc);
      m11.setMonth(m11.getMonth() + 11);
      const m11Offset = (m11 - timeRange.minDate) / (1000 * 60 * 60 * 24);
      ms.push({ label: 'M11 Walk', pct: (m11Offset / timeRange.totalDays) * 100, color: '#a855f7' });
    }
    return ms.filter(m => m.pct >= 0 && m.pct <= 100);
  }, [timeRange, substantialCompletion]);

  const handleExportPDF = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/warranty-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `warranty_calendar_${projectId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-32">
      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="warranty-calendar">
      {/* Filter */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {['all', 'active', 'expiring_soon', 'expired'].map(f => (
            <button key={f} onClick={() => setStatusFilter(f)}
              className={`px-2.5 py-1 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
                statusFilter === f ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
              }`}>
              {f === 'all' ? 'All' : f.replace('_', ' ')}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{filtered.length} warranties with dates</span>
          <button
            onClick={handleExportPDF}
            data-testid="warranty-pdf-export-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider text-emerald-400 border border-emerald-800/40 hover:bg-emerald-950/30 transition-colors"
          >
            <Download className="w-3 h-3" />
            Export PDF
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-card border border-dashed border-border rounded-sm">
          <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No warranties with date ranges to display</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm p-4 overflow-x-auto">
          {/* Time axis */}
          <div className="flex items-center gap-3 mb-2 h-6">
            <div className="w-48 shrink-0" />
            <div className="flex-1 relative h-6">
              {yearMarkers.map(m => (
                <div key={m.year} className="absolute top-0 h-full" style={{ left: `${m.pct}%` }}>
                  <div className="w-px h-full bg-secondary" />
                  <span className="absolute top-0 text-[9px] font-mono text-muted-foreground -translate-x-1/2">{m.year}</span>
                </div>
              ))}
              {/* Milestone markers */}
              {milestones.map((m, i) => (
                <div key={i} className="absolute top-0 h-full z-10" style={{ left: `${m.pct}%` }}>
                  <div className="w-px h-full" style={{ backgroundColor: m.color, opacity: 0.6 }} />
                  <span className="absolute -top-0.5 text-[8px] font-mono whitespace-nowrap -translate-x-1/2"
                    style={{ color: m.color }}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Warranty bars */}
          <div className="space-y-0.5 mt-4">
            {filtered.map(w => (
              <WarrantyBar key={w.id} w={w} timeRange={timeRange}
                selected={selected?.id === w.id}
                onClick={() => setSelected(selected?.id === w.id ? null : w)} />
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border">
            {[
              { label: 'Active', color: STATUS_COLORS.active },
              { label: 'Expiring Soon', color: STATUS_COLORS.expiring_soon },
              { label: 'Expired', color: STATUS_COLORS.expired },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: l.color }} />
                {l.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div className="mt-4 p-4 bg-card border border-border rounded-sm" data-testid="calendar-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-heading font-bold text-foreground">{selected.equipment_name}</h3>
            <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground/80"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><span className="text-muted-foreground">Vendor:</span> <span className="text-foreground/80 ml-1">{selected.vendor_name || '—'}</span></div>
            <div><span className="text-muted-foreground">Type:</span> <span className="text-foreground/80 ml-1">{selected.warranty_type || '—'}</span></div>
            <div><span className="text-muted-foreground">Status:</span> <span className={`ml-1 ${
              selected.status === 'active' ? 'text-emerald-400' : selected.status === 'expiring_soon' ? 'text-amber-400' : selected.status === 'expired' ? 'text-red-400' : 'text-muted-foreground'
            }`}>{selected.status}</span></div>
            <div><span className="text-muted-foreground">Start:</span> <span className="font-mono text-foreground/80 ml-1">{selected.warranty_start || '—'}</span></div>
            <div><span className="text-muted-foreground">End:</span> <span className="font-mono text-foreground/80 ml-1">{selected.warranty_end || '—'}</span></div>
            <div><span className="text-muted-foreground">Days Left:</span> <span className={`font-mono ml-1 ${
              selected.days_remaining < 0 ? 'text-red-400' : selected.days_remaining <= 90 ? 'text-amber-400' : 'text-emerald-400'
            }`}>{selected.days_remaining ?? '—'}</span></div>
          </div>
          {selected.coverage_details && (
            <p className="text-xs text-muted-foreground mt-2">{selected.coverage_details}</p>
          )}
        </div>
      )}
    </div>
  );
}
